import notificationService from '@/services/notificationService';
import NotificationItem from '@/model/notification';
import {
  filterNotificationsForUser,
  notificationIncludesMobile,
  sortNotificationsByNewest
} from '@/helper/notificationHelper';

const state = {
  notifications: [],
  notificationCount: 0,
  loading: false,
  loaded: false,
  patchingId: null
};

const actions = {
  list({ commit, rootState, rootGetters }) {
    const { authKey } = rootState.auth;
    const user = rootGetters['auth/getUserData'];
    const mobile = user?.mobile;

    if (!authKey || mobile == null || mobile === '') {
      return Promise.resolve();
    }

    commit('startRequest');

    return notificationService
      .list({ authKey })
      .then(response => {
        const items = Array.isArray(response) ? response : response?.rows || response?.data || [];
        const { visible, unreadCount } = filterNotificationsForUser(items, mobile);

        commit('setNotifications', {
          notifications: sortNotificationsByNewest(
            visible.map(item => new NotificationItem(item, { mobile }))
          ),
          notificationCount: unreadCount
        });
      })
      .catch(e => {
        console.error(e);
        commit('requestFailed');
      });
  },
  clearNotifications({ commit }) {
    commit('clearNotifications');
  },

  patchOne({ commit, rootState, rootGetters }, { item, type = 'read' }) {
    const { authKey } = rootState.auth;
    const user = rootGetters['auth/getUserData'];
    const mobile = user?.mobile;
    const id = item?.id || item?.raw?._id;

    if (!authKey || !id || mobile == null || mobile === '' || item?.isRead) {
      return Promise.resolve();
    }

    commit('startPatchRequest', { id });

    return notificationService
      .patchOne({ id, authKey, type })
      .then(response => {
        const nModified = response?.nModified ?? response?.data?.nModified;
        if (nModified === 1) {
          commit('markNotificationRead', { item, mobile, type });
        }
      })
      .catch(e => {
        console.error(e);
      })
      .finally(() => {
        commit('finishPatchRequest');
      });
  }
};

const getters = {
  notifications: state => state.notifications,
  notificationCount: state => state.notificationCount,
  loading: state => state.loading,
  patchingId: state => state.patchingId
};

const mutations = {
  startRequest(state) {
    state.loading = true;
  },
  requestFailed(state) {
    state.loading = false;
  },
  setNotifications(state, { notifications, notificationCount }) {
    state.loading = false;
    state.loaded = true;
    state.notifications = notifications;
    state.notificationCount = notificationCount;
  },
  clearNotifications(state) {
    state.notifications = [];
    state.notificationCount = 0;
    state.loading = false;
    state.loaded = false;
    state.patchingId = null;
  },
  startPatchRequest(state, { id }) {
    state.patchingId = id;
  },
  finishPatchRequest(state) {
    state.patchingId = null;
  },
  markNotificationRead(state, { item, mobile }) {
    const notification = state.notifications.find(entry => entry.id === item.id);
    if (!notification || notification.isRead) {
      return;
    }

    notification.isRead = true;
    if (!notification.raw) {
      notification.raw = {};
    }
    if (!Array.isArray(notification.raw.readBy)) {
      notification.raw.readBy = [];
    }
    if (!notificationIncludesMobile(notification.raw.readBy, mobile)) {
      notification.raw.readBy.push(mobile);
    }

    state.notificationCount = Math.max(0, state.notificationCount - 1);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
